<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-50 overflow-hidden pointer-events-none">
      <!-- Background overlay with blur -->
      <Transition name="fade">
        <div 
          v-if="isOpen"
          class="absolute inset-0 bg-noir-950/80 backdrop-blur-sm pointer-events-auto"
          @click="$emit('close')"
        ></div>
      </Transition>

      <Transition name="drawer">
        <div 
          v-if="isOpen" 
          class="fixed inset-y-0 right-0 flex max-w-full pl-10 pointer-events-auto"
        >
          <div class="w-screen max-w-md transform">
            <div class="flex h-full flex-col overflow-y-scroll bg-noir-900/95 backdrop-blur-lg shadow-2xl border-l border-noir-700">
              <div class="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
              <div class="flex items-start justify-between">
                <h2 class="text-lg font-display font-medium text-cream-100">{{ t('cart.title') }}</h2>
                <div class="ml-3 flex h-7 items-center">
                  <button 
                    type="button" 
                    class="-m-2 p-2 text-noir-400 hover:text-gold-400 transition-colors" 
                    @click="$emit('close')"
                  >
                    <span class="sr-only">{{ t('common.close') }}</span>
                    <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div class="mt-8">
                <div v-if="items.length === 0" class="text-center py-12">
                  <svg class="mx-auto h-12 w-12 text-noir-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <p class="mt-4 text-noir-400 font-sans">{{ t('cart.empty.title') }}</p>
                </div>

                <div v-else class="flow-root">
                  <ul role="list" class="-my-6 divide-y divide-noir-800">
                    <li v-for="item in items" :key="item.id" class="py-6">
                      <PublicCartItem 
                        :item="item" 
                        :loading="loading"
                        @update:quantity="(id, qty) => $emit('update:quantity', id, qty)"
                        @remove="id => $emit('remove', id)"
                      />
                    </li>
                  </ul>
                </div>
              </div>
            </div>

              <div class="border-t border-noir-700 px-4 py-6 sm:px-6">
                <PublicCartSummary :subtotal="subtotal" :discount-amount="discountAmount" :total="total" />
                
                <div class="mt-6">
                  <BaseButton
                    variant="primary"
                    fullWidth
                    @click="goToCheckout"
                    :disabled="items.length === 0"
                  >
                    {{ t('cart.proceedToCheckout') }}
                  </BaseButton>
                </div>
                <div class="mt-3">
                  <BaseButton
                    variant="outline-contrast"
                    fullWidth
                    @click="goToCart"
                  >
                    {{ t('cart.viewCart') }}
                  </BaseButton>
                </div>
                <div class="mt-6 flex justify-center text-center text-sm text-noir-400 font-sans">
                  <p>
                    {{ t('cart.or') }}
                    <button 
                      type="button" 
                      class="font-medium text-gold-400 hover:text-gold-300 transition-colors" 
                      @click="$emit('close')"
                    >
                      {{ t('cart.continueShopping') }}
                      <span aria-hidden="true"> &rarr;</span>
                    </button>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import type { CartItem } from '@/modules/cart/domain/entities/CartItem';
import PublicCartItem from './PublicCartItem.vue';
import PublicCartSummary from './PublicCartSummary.vue';
import BaseButton from '@/lib/ui/components/BaseButton.vue';

defineProps<{
  isOpen: boolean;
  items: CartItem[];
  subtotal: number;
  discountAmount?: number;
  total: number;
  loading?: boolean;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'update:quantity', id: string, quantity: number): void;
  (e: 'remove', id: string): void;
}>();

const router = useRouter();
const { t } = useI18n();

const goToCheckout = () => {
  emit('close');
  router.push('/checkout');
};

const goToCart = () => {
  emit('close');
  router.push('/cart');
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.drawer-enter-active,
.drawer-leave-active {
  transition: transform 0.3s ease, opacity 0.2s ease;
}
.drawer-enter-from,
.drawer-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
</style>
